import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  addMutedKey,
  announcementAudioKey,
  parseMutedKeys,
  removeMutedKey,
  type AnnouncementWithAudio,
} from "@/lib/announcement-audio";

/**
 * Per-browser announcement-sound preferences for the member portal.
 *
 * - **Opt-in** (``enabled``): browsers block audible autoplay until the user
 *   interacts with the page, so sound is off until the member presses
 *   "Enable alarm sounds" once. That click is itself the user gesture that
 *   unlocks playback. Remembered in localStorage for this browser.
 * - **Per-announcement mute**: a member who has heard an announcement can
 *   silence it. Keyed by ``announcementAudioKey`` (id + raised_at + play_at),
 *   so a later re-raise of the same row plays again. localStorage too — it's
 *   this member's browser, never the backend, and never affects anyone else.
 *
 * Every storage access is wrapped: private windows / blocked site data make it
 * throw, and the portal must then just behave as "not enabled, nothing muted".
 */
const ENABLED_KEY = "nodus.portal.announcementSound.enabled";
const MUTED_KEY = "nodus.portal.announcementSound.muted";

function readStorage(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string | null): void {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    // Storage unavailable — the choice just won't survive a reload.
  }
}

interface AnnouncementSoundContextValue {
  enabled: boolean;
  setEnabled: (enabled: boolean) => void;
  mutedKeys: ReadonlySet<string>;
  isMuted: (a: AnnouncementWithAudio) => boolean;
  mute: (a: AnnouncementWithAudio) => void;
  unmute: (a: AnnouncementWithAudio) => void;
}

const AnnouncementSoundContext = createContext<AnnouncementSoundContextValue | null>(null);

export function AnnouncementSoundProvider({ children }: { children: ReactNode }) {
  const [enabled, setEnabledState] = useState(() => readStorage(ENABLED_KEY) === "1");
  const [muted, setMuted] = useState<string[]>(() => parseMutedKeys(readStorage(MUTED_KEY)));

  // Another tab muted / enabled → follow it, so muting in one tab silences all.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === ENABLED_KEY) setEnabledState(e.newValue === "1");
      if (e.key === MUTED_KEY) setMuted(parseMutedKeys(e.newValue));
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const setEnabled = useCallback((next: boolean) => {
    setEnabledState(next);
    writeStorage(ENABLED_KEY, next ? "1" : null);
  }, []);

  const updateMuted = useCallback((fn: (prev: string[]) => string[]) => {
    setMuted((prev) => {
      const next = fn(prev);
      writeStorage(MUTED_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const mute = useCallback(
    (a: AnnouncementWithAudio) => updateMuted((prev) => addMutedKey(prev, announcementAudioKey(a))),
    [updateMuted],
  );
  const unmute = useCallback(
    (a: AnnouncementWithAudio) => updateMuted((prev) => removeMutedKey(prev, announcementAudioKey(a))),
    [updateMuted],
  );

  const mutedKeys = useMemo(() => new Set(muted), [muted]);
  const isMuted = useCallback((a: AnnouncementWithAudio) => mutedKeys.has(announcementAudioKey(a)), [mutedKeys]);

  const value = useMemo(
    () => ({ enabled, setEnabled, mutedKeys, isMuted, mute, unmute }),
    [enabled, setEnabled, mutedKeys, isMuted, mute, unmute],
  );
  return <AnnouncementSoundContext.Provider value={value}>{children}</AnnouncementSoundContext.Provider>;
}

export function useAnnouncementSound(): AnnouncementSoundContextValue {
  const ctx = useContext(AnnouncementSoundContext);
  if (!ctx) {
    throw new Error("useAnnouncementSound must be used within an AnnouncementSoundProvider");
  }
  return ctx;
}
